import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Check,
  Loader2,
  QrCode,
  RefreshCw,
  ShieldCheck,
  Smartphone,
  X,
} from 'lucide-react';
import type { QRLoginStatus } from '../types';
import {
  checkQRLoginStatus,
  generateQRLogin,
} from '../services/api';

type ViewState = 'loading' | QRLoginStatus;

interface QRLoginModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

const POLL_INTERVAL_MS = 2_000;
const SUCCESS_DELAY_MS = 1_000;
const MAX_CONSECUTIVE_FAILURES = 3;

const TERMINAL_MESSAGES: Partial<Record<QRLoginStatus, string>> = {
  expired: '二维码已过期',
  cancelled: '已取消登录',
  not_found: '登录会话不存在',
  error: '登录状态检查失败',
};

const QRLoginModal: React.FC<QRLoginModalProps> = ({
  onClose,
  onSuccess,
}) => {
  const [status, setStatus] = useState<ViewState>('loading');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [faceQrUrl, setFaceQrUrl] = useState('');
  const [message, setMessage] = useState('正在生成二维码…');

  const generationRef = useRef(0);
  const currentStatusRef = useRef<ViewState>('loading');
  const pollTimerRef = useRef<number | null>(null);
  const closeTimerRef = useRef<number | null>(null);
  const pollingRef = useRef(false);

  const updateStatus = useCallback((nextStatus: ViewState) => {
    currentStatusRef.current = nextStatus;
    setStatus(nextStatus);
  }, []);

  const clearTimers = useCallback(() => {
    if (pollTimerRef.current !== null) {
      window.clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const beginLogin = useCallback(async () => {
    const generation = generationRef.current + 1;
    generationRef.current = generation;
    clearTimers();
    pollingRef.current = false;
    setQrCodeUrl('');
    setFaceQrUrl('');
    setMessage('正在生成二维码…');
    updateStatus('loading');

    try {
      const generated = await generateQRLogin();
      if (generation !== generationRef.current) return;

      if (
        !generated.success ||
        !generated.session_id ||
        !generated.qr_code_url
      ) {
        setMessage(generated.message || '获取二维码失败');
        updateStatus('error');
        return;
      }

      const sessionId = generated.session_id;
      setQrCodeUrl(generated.qr_code_url);
      setMessage('请打开闲鱼 APP 扫描二维码');
      updateStatus('waiting');

      let consecutiveFailures = 0;

      const scheduleNextPoll = () => {
        if (generation !== generationRef.current) return;
        pollTimerRef.current = window.setTimeout(
          () => void pollStatus(),
          POLL_INTERVAL_MS,
        );
      };

      const finishSuccess = () => {
        if (pollTimerRef.current !== null) {
          window.clearTimeout(pollTimerRef.current);
          pollTimerRef.current = null;
        }
        setMessage('账号授权已保存');
        updateStatus('success');
        closeTimerRef.current = window.setTimeout(() => {
          if (generation !== generationRef.current) return;
          onSuccess();
          onClose();
        }, SUCCESS_DELAY_MS);
      };

      const pollStatus = async () => {
        if (
          generation !== generationRef.current ||
          pollingRef.current
        ) {
          return;
        }

        pollingRef.current = true;
        let shouldContinue = true;

        try {
          const result = await checkQRLoginStatus(sessionId);
          if (generation !== generationRef.current) return;
          consecutiveFailures = 0;

          if (
            result.status === 'success' ||
            result.status === 'already_processed'
          ) {
            shouldContinue = false;
            finishSuccess();
          } else if (result.status === 'scanned') {
            if (currentStatusRef.current !== 'verification_required') {
              setMessage('已扫码，请在手机上确认');
              updateStatus('scanned');
            }
          } else if (result.status === 'verification_required') {
            if (result.face_qr_url) {
              setFaceQrUrl(result.face_qr_url);
            }
            setMessage('闲鱼要求完成一次人脸验证');
            updateStatus('verification_required');
          } else if (
            result.status === 'waiting' ||
            result.status === 'processing'
          ) {
            if (currentStatusRef.current !== 'verification_required') {
              setMessage('请打开闲鱼 APP 扫描二维码');
              updateStatus('waiting');
            }
          } else {
            shouldContinue = false;
            setMessage(
              TERMINAL_MESSAGES[result.status] ||
                result.message ||
                '登录状态检查失败',
            );
            updateStatus(result.status);
          }
        } catch {
          consecutiveFailures += 1;
          if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
            shouldContinue = false;
            setMessage('网络连接不稳定，请重新扫码');
            updateStatus('error');
          }
        } finally {
          pollingRef.current = false;
        }

        if (shouldContinue && generation === generationRef.current) {
          scheduleNextPoll();
        }
      };

      scheduleNextPoll();
    } catch (error) {
      if (generation !== generationRef.current) return;
      setMessage(
        error instanceof Error && error.message
          ? error.message
          : '获取二维码失败',
      );
      updateStatus('error');
    }
  }, [clearTimers, onClose, onSuccess, updateStatus]);

  useEffect(() => {
    void beginLogin();
    return () => {
      generationRef.current += 1;
      clearTimers();
    };
  }, [beginLogin, clearTimers]);

  const handleClose = () => {
    generationRef.current += 1;
    clearTimers();
    onClose();
  };

  const isTerminalError =
    status === 'expired' ||
    status === 'cancelled' ||
    status === 'not_found' ||
    status === 'error';
  const showQrCode =
    status === 'waiting' ||
    status === 'processing' ||
    status === 'scanned';

  return (
    <div className="modal-overlay-centered" role="presentation">
      <section
        aria-labelledby="qr-login-title"
        aria-modal="true"
        className="modal-container"
        role="dialog"
        style={{ maxWidth: '28rem' }}
      >
        <button
          aria-label="关闭扫码登录"
          className="self-end p-2 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors mb-4"
          onClick={handleClose}
          type="button"
        >
          <X className="w-5 h-5 text-gray-600" />
        </button>

        <div className="modal-body text-center">
          <div className="w-12 h-12 rounded-2xl bg-[#FFE815] mx-auto mb-4 flex items-center justify-center shadow-lg shadow-yellow-100">
            <QrCode className="w-6 h-6 text-gray-900" />
          </div>
          <h3
            className="text-2xl font-extrabold text-gray-900 mb-2"
            id="qr-login-title"
          >
            扫码登录闲鱼
          </h3>
          <p className="text-gray-500 mb-6 font-medium">
            {isTerminalError
              ? '当前登录未完成'
              : status === 'scanned'
                ? '二维码识别成功'
                : message}
          </p>

          <div className="min-h-64 bg-[#F7F8FA] rounded-[2rem] mx-auto flex items-center justify-center overflow-hidden border-4 border-white shadow-inner mb-6 relative">
            {status === 'loading' && (
              <div className="flex flex-col items-center gap-3 text-gray-500">
                <Loader2 className="w-10 h-10 text-[#E4CE00] animate-spin" />
                <span className="text-sm font-semibold">正在安全连接闲鱼</span>
              </div>
            )}

            {showQrCode && (
              <img
                alt="闲鱼登录二维码"
                className="w-64 h-64 p-3"
                src={qrCodeUrl}
              />
            )}

            {status === 'scanned' && (
              <div className="absolute inset-0 bg-white/95 flex flex-col items-center justify-center px-6 text-blue-700 animate-fade-in">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <Smartphone className="w-8 h-8" />
                </div>
                <span className="font-bold text-lg">
                  已扫码，请在手机上确认
                </span>
                <span className="text-sm text-gray-500 mt-2">
                  确认后本页会自动完成登录
                </span>
              </div>
            )}

            {status === 'verification_required' && (
              <div className="w-full px-5 py-6 flex flex-col items-center text-amber-700 animate-fade-in">
                <span className="font-bold text-lg mb-2">需要安全验证</span>
                {faceQrUrl ? (
                  <>
                    <img
                      alt="闲鱼人脸验证二维码"
                      className="w-52 h-52 rounded-2xl bg-white p-2 border border-amber-100"
                      src={faceQrUrl}
                    />
                    <p className="text-sm text-gray-500 leading-6 mt-3">
                      请使用手机闲鱼 APP 再次扫码完成人脸验证
                    </p>
                    <div className="mt-2 flex items-center gap-2 text-xs font-semibold text-amber-600">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>验证完成后将自动登录，请勿关闭窗口</span>
                    </div>
                  </>
                ) : (
                  <div className="py-8 flex flex-col items-center gap-3">
                    <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center">
                      <ShieldCheck className="w-7 h-7" />
                    </div>
                    <Loader2 className="w-7 h-7 animate-spin" />
                    <span className="text-sm font-semibold text-gray-500">
                      正在获取人脸验证二维码
                    </span>
                  </div>
                )}
              </div>
            )}

            {status === 'success' && (
              <div className="absolute inset-0 bg-white/95 flex flex-col items-center justify-center text-green-600 animate-fade-in">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <Check className="w-8 h-8" />
                </div>
                <span className="font-bold text-lg">登录成功</span>
                <span className="text-sm text-gray-500 mt-2">
                  正在刷新账号列表
                </span>
              </div>
            )}

            {isTerminalError && (
              <div className="flex flex-col items-center px-6">
                <div className="w-14 h-14 rounded-full bg-red-50 text-red-500 flex items-center justify-center mb-4">
                  <X className="w-7 h-7" />
                </div>
                <span className="text-red-600 font-bold mb-4">
                  {message}
                </span>
                <button
                  className="bg-gray-200 hover:bg-gray-300 px-4 py-2 rounded-xl flex items-center gap-2 font-bold text-sm text-gray-700"
                  onClick={() => void beginLogin()}
                  type="button"
                >
                  <RefreshCw className="w-4 h-4" />
                  重新扫码
                </button>
              </div>
            )}
          </div>

          <p className="text-xs text-gray-400 font-medium bg-gray-50 py-2.5 px-4 rounded-xl">
            {status === 'verification_required'
              ? '人脸验证由闲鱼官方处理，本系统不会保存人脸信息。'
              : '二维码有效期为 5 分钟，请仅使用闲鱼官方 APP 扫描。'}
          </p>
        </div>
      </section>
    </div>
  );
};

export default QRLoginModal;
